import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type Dispatch,
  type FormEvent,
  type SetStateAction,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AccountingScheme,
  OwnerEntity,
  RobotStatus,
  ROBOT_STATUS_LABELS,
  UserRole,
  VerificationStatus,
  LocationCategory,
  SERIAL_LONG_RE,
  composeSerialLong,
  deriveSerialShortFromLong,
  isTransitionSelectable,
  isVerificationStatusAllowedForTransition,
  productionWeekFromDate,
  LoaderStatus,
} from '@bhops/shared';
import type { EcnSummaryDto, ListModelsResponse } from '@bhops/shared';
import type { CreateRobotBody, UpdateRobotBody } from '@/api/robots';
import { loadersApi } from '@/api/loaders';
import { modelsApi } from '@/api/models';
import { ecnsApi } from '@/api/ecns';
import { Field, Input, Select, Textarea } from '@/components/Form';
import { EditableSelect } from '@/components/EditableSelect';
import { ModelPicker } from '@/components/pickers/ModelPicker';
import { Icon } from '@/components/Icon';
import { Tooltip } from '@/components/Tooltip';
import { useAuthStore } from '@/stores/auth';
// 추가수정 2차(26-07-27) 정정 — 표시 날짜는 KST 정본으로.
import { kstYmd } from '@/lib/format-date';

export interface RobotFormValues {
  serialShort?: string;
  serialLong?: string;
  modelCode: string;
  status: RobotStatus;
  locationCategory: LocationCategory;
  verificationStatus: VerificationStatus;
  // §D-1 / Q#5 — accounting fields kept in the type so existing data
  // round-trips through the form without loss, but the inputs are
  // hidden in the UI per PDF p.8.
  accountingBookNo?: string;
  accountingScheme: AccountingScheme;
  ownerEntity: OwnerEntity;
  firstProductionYm?: string;
  firstShipmentYm?: string;
  // §D-1 (PDF p.8) — new operator-editable trace fields. Strings come
  // from the OptionGroup table (rendered as a Select); date fields are
  // ISO YYYY-MM-DD via <input type="date">.
  color?: string;
  // FB2 §5C — loaderType 제거(구분 사양과 중복). DB 컬럼은 보존하나 폼은 미사용.
  manufactureCompletedAt?: string;
  binaryUpdatedAt?: string;
  // round5 #4 — 등록 시 BINARY 펌웨어 이벤트의 버전(선택, create 전용).
  binaryVersion?: string;
  // S2 Phase 2.2 — 자동 SN Composer 의 내구성 입력. productionDate(ISO
  // yyyy-mm-dd)는 생산주(YYWW) 자동 산출의 원천, sensorType(F/N)은 Long-SN
  // ColorMode 의 두 번째 글자. 둘 다 DB 컬럼이 있어 create body 로 전송.
  productionDate?: string;
  sensorType?: string;
  // FB §2 — "구분 사양"(모델 축 값). 선택 모델의 variantOptionGroupKey 가
  // 있을 때만 폼에 노출. DB 컬럼 Robot.variantValue 로 영속.
  variantValue?: string;
  notes?: string;
  // 개선계획 §4 — 등록 창에서 고른 '적용 변경점'(ECN id). create 모드 전용:
  // 편집은 기존대로 로봇 상세의 부품 탭에서 관리한다.
  ecnIds?: string[];
  // 계획서(2026-08-27) 요구 4 — 등록 창에서 함께 장착할 로더(선택). create 전용:
  // 이미 있는 로봇의 로더는 교체 이력이 중요해 로봇 상세의 로더 창이 단일 창구다.
  loaderUnitId?: string;
}

interface Props {
  formId: string;
  values: RobotFormValues;
  mode: 'create' | 'edit';
  /**
   * round11 요구2 — 편집 진입 시점의 서버 상태(폼 편집 전 원본). 상태 select 를
   * "여기서 실제로 갈 수 있는 상태"로 좁히는 데 쓴다. 서버가 전이 가드를
   * 검사할 때 쓰는 값과 같아야 하므로 **폼의 현재 값이 아니라 원본**이다:
   * 검증 상태를 같은 요청에서 PHANTOM→CONFIRMED 로 바꿔도 서버는 DB 행의
   * 값으로 판정하므로(PATCH 내 자기 승격 차단), 폼 값으로 판단하면 UI 가 또
   * 거짓말을 하게 된다. create 모드에선 전이가 아니라 초기값이므로 미사용.
   */
  origin?: { status: RobotStatus; verificationStatus: VerificationStatus };
  /**
   * 함수형 업데이트(useState setter)를 그대로 받는다 — 폼 안의 여러 하위
   * 컴포넌트(SN 컴포저, 변경점 피커)가 같은 커밋에서 각자 갱신을 내보내면,
   * 값 객체를 통째로 넘길 경우 뒤 호출이 앞 호출의 필드를 덮어쓴다(둘 다 같은
   * stale 스냅샷을 spread — round5 #3b 와 같은 클래스의 버그).
   */
  onChange: Dispatch<SetStateAction<RobotFormValues>>;
  onSubmit: () => void;
}

// FB2 §1 (결정 F) — 수동 폼에 노출하는 상태 집합. INSTALL_PENDING/IN_STORAGE 는
// 시스템 전이 상태라 제외한다(필터에서는 노출).
const MANUAL_STATUS_CHOICES: RobotStatus[] = [
  RobotStatus.IN_PRODUCTION,
  RobotStatus.READY_TO_SHIP,
  RobotStatus.DEPLOYED,
  RobotStatus.IN_AS,
  // round10 요구2 — 폐기 예정(장부 잔존)/완료(상각) 수동 선택.
  RobotStatus.RETIREMENT_PLANNED,
  RobotStatus.RETIRED,
];

export function RobotForm({ formId, values, mode, origin, onChange, onSubmit }: Props) {
  const user = useAuthStore((s) => s.user);
  const entityDisabled = user?.role !== UserRole.ADMIN;
  // 초기 데이터 보정 — 회계 장부 No 는 ADMIN 전용 필드(백엔드 @DeniedForRole).
  // 첫 제조/출고 월은 비제한이지만 입력 섹션을 ADMIN 정정 영역에 함께 둔다.
  const isAdmin = user?.role === UserRole.ADMIN;
  // round5 #3a — SN 직접입력 토글을 폼 레벨로 승격: 컴포저(자동) 모드에선
  // 생산일자가 제조 완료일로 서버 파생되므로 '제조 완료일' 입력을 숨긴다.
  // 직접입력 모드(생산일자 입력 자체가 없음)에선 입력을 유지해야 한다.
  const [snManual, setSnManual] = useState(false);

  // FB §2 — 선택 모델의 "구분 사양" 축을 알아야 variantValue picker 를 렌더할 수
  // 있다. SnComposer 와 동일한 queryKey(['models']) 를 사용하므로 react-query 가
  // 디듀프한다(추가 네트워크 없음). edit 모드에도 필요해 폼 레벨에서 조회.
  const { data: modelsData } = useQuery<ListModelsResponse>({
    queryKey: ['models'],
    queryFn: () => modelsApi.list(),
    staleTime: 5 * 60_000,
  });
  const selectedModel = (modelsData?.items ?? []).find((m) => m.code === values.modelCode);

  // round11 요구2 — 상태 select 를 실제 전이 가능한 값으로 좁힌다.
  //
  // 종전엔 6개 상태를 전부 노출했다. '제조 중'(초기 매핑 CSV '연구개발'의 lossy
  // 변환분)에서 '운영중'을 고르면 서버가 422 로 거부하고 **PATCH 전체가 롤백**돼
  // 함께 고친 SN·제조월·비고까지 사라졌다 — 화면상으론 "그냥 안 먹네". 고를 수
  // 없는 것은 애초에 보여주지 않고, 왜 좁혀졌는지는 아래 hint 로 알린다.
  //
  // 원본 상태(origin.status)는 값이 렌더돼야 하므로 필터와 무관하게 항상 포함.
  const statusChoices = useMemo<RobotStatus[]>(() => {
    if (mode === 'create' || !origin || !user) return MANUAL_STATUS_CHOICES;
    const reachable = MANUAL_STATUS_CHOICES.filter(
      (to) =>
        to === origin.status ||
        isTransitionSelectable({
          from: origin.status,
          to,
          role: user.role,
          verificationStatus: origin.verificationStatus,
        }),
    );
    // ⚠ 2026-09-23 — 위 필터는 MANUAL_STATUS_CHOICES **안에서만** 원본을 살린다.
    //   원본이 그 목록 밖이면(기타·재고·설치 대기) 선택지에 없어서 브라우저가 **첫 항목
    //   ('출고 대기')을 현재 상태처럼** 보여 줬다. 그 상태에서 '출고 대기' 를 고르면 바뀐
    //   것이 없다고 보고 아무것도 안 보내 조용히 그대로였다. 원본은 늘 맨 앞에 둔다.
    return reachable.includes(origin.status) ? reachable : [origin.status, ...reachable];
  }, [mode, origin, user]);

  // 좁혀진 이유 안내(한글). 서버 422 를 읽고 나서야 아는 대신 미리 알린다.
  const statusHint = useMemo<string | undefined>(() => {
    if (mode === 'create' || !origin || !user) return undefined;
    const parts = [
      `지금 상태인 '${ROBOT_STATUS_LABELS[origin.status]}'에서 바꿀 수 있는 상태만 보입니다.`,
    ];
    // INV-03 — 미확인 자산(PHANTOM)은 출고/설치/배치 진입 자체가 막힌다. 초기
    // 매핑 로봇이 여기 걸리는 주된 이유라 별도로 짚어준다.
    const phantomBlocked = MANUAL_STATUS_CHOICES.filter(
      (to) => !isVerificationStatusAllowedForTransition(to, origin.verificationStatus),
    );
    if (phantomBlocked.length > 0) {
      parts.push(
        `검증 상태가 '미확인 자산'이라 ${phantomBlocked
          .map((s) => `'${ROBOT_STATUS_LABELS[s]}'`)
          .join(
            ', ',
          )} 상태는 잠겨 있습니다. 실물을 확인한 뒤 검증 상태를 '확인됨'으로 먼저 저장해 주세요.`,
      );
    }
    if (user.role !== UserRole.ADMIN) {
      parts.push(`'${ROBOT_STATUS_LABELS.RETIRED}' 상태는 관리자만 지정할 수 있습니다.`);
    }
    return parts.join(' ');
  }, [mode, origin, user]);

  function set<K extends keyof RobotFormValues>(key: K, value: RobotFormValues[K]): void {
    onChange((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    onSubmit();
  }

  return (
    <form id={formId} onSubmit={handleSubmit} noValidate>
      {/* §D-1 #2 (PDF p.8) — SN now editable on both create and edit.
          Server-side @unique enforcement is unchanged; the integrity
          warning dialog is implemented at submit time in the parent
          modal (RobotEditModal) — not here.
          v1.0.14 §6 — Short/Long SN 입력 안내 명확화. 두 SN 중 최소 하나는
          필수 (서버 측 CHECK constraint).
          S2 Phase 2.2 — edit 모드는 기존 Short/Long raw input 2-컬럼 그리드를
          그대로 유지. create 모드는 Short SN(raw) + Long SN(자동 Composer)
          로 분기해 운영자가 원시 문자열을 직접 타이핑하지 않도록 한다. */}
      {mode === 'edit' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field
            label="Short SN"
            htmlFor="serialShort"
            hint="바꾸면 연결된 기록에도 영향이 있으니 신중히 바꿔 주세요"
          >
            <Input
              id="serialShort"
              value={values.serialShort ?? ''}
              onChange={(e) => set('serialShort', e.target.value)}
              placeholder="예: 123"
              inputMode="numeric"
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </Field>

          <Field
            label="Long SN"
            htmlFor="serialLong"
            hint="바꾸면 연결된 기록에도 영향이 있으니 신중히 바꿔 주세요"
          >
            <Input
              id="serialLong"
              value={values.serialLong ?? ''}
              onChange={(e) => set('serialLong', e.target.value)}
              placeholder="예: GEN2-2613-WF207"
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </Field>
        </div>
      ) : (
        <>
          <Field
            label="Short SN"
            htmlFor="serialShort"
            hint="123이나 12-34처럼 입력합니다. Long SN을 입력하면 비워 둬도 됩니다."
          >
            <Input
              id="serialShort"
              value={values.serialShort ?? ''}
              onChange={(e) => set('serialShort', e.target.value)}
              placeholder="예: 123"
              inputMode="numeric"
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </Field>

          <SnComposer
            values={values}
            set={set}
            patch={(p) => onChange((prev) => ({ ...prev, ...p }))}
            manual={snManual}
            onManualChange={setSnManual}
          />
        </>
      )}

      {/*
       * windows-ux-edit (2026-06) — 모델 소스 단일화.
       * 기존: OptionGroup `robot.model` 기반 EditableSelect — 그러나 그 그룹은
       * 어디서도 seed 되지 않아(스쿼시 때 시드 마이그레이션 유실) 드롭다운이 빈
       * 채로 출시됨 + CustomerMaster(상수)와 소스 불일치("두 모달 미연동").
       * 변경: 정전 소스인 `Model` 레지스트리(GET /models)를 소비하는 공용
       * ModelPicker 로 통일. SN 조립/variant 로직이 Model.code 매칭이므로 정합.
       * 모델 추가/편집은 관리자 모델 관리(별도 follow-up)로 일원화.
       */}
      <Field
        label="모델"
        htmlFor="modelCode"
        required
        hint="모델 추가와 편집은 관리자 메뉴의 모델 관리에서 합니다"
      >
        <ModelPicker
          id="modelCode"
          emit="code"
          value={values.modelCode}
          onChange={(v) => set('modelCode', v || 'UNKNOWN')}
        />
      </Field>

      {/*
       * FB §2 — "구분 사양"(variantValue). 모델마다 핵심 구분 축이 다르므로
       * (GEN2 방향 / GEN1 통신 케이블 / Zephyr 타입) 선택 모델의
       * variantOptionGroupKey 로 picklist 를 구동한다. 라벨은 모델의
       * variantAxisLabel(없으면 '구분 사양'). 축이 없는 모델(SEN/HEADLESS 등,
       * variantOptionGroupKey == null)은 필드를 숨긴다. create/edit 공통.
       */}
      {selectedModel?.variantOptionGroupKey && (
        <Field
          label={selectedModel.variantAxisLabel ?? '구분 사양'}
          htmlFor="variantValue"
          hint="모델을 나누는 기준입니다. 매니저는 '+ 새 옵션 추가…'로 항목을 늘릴 수 있습니다"
        >
          <EditableSelect
            id="variantValue"
            groupKey={selectedModel.variantOptionGroupKey}
            value={values.variantValue ?? ''}
            onChange={(v) => set('variantValue', v)}
            placeholder="— 선택 —"
          />
        </Field>
      )}

      {/*
       * 개선계획 §4 — 등록 창에서 바로 '적용 변경점'을 고른다. 종전엔 로봇을
       * 만든 뒤 상세 → 부품 탭에서 하나씩 태깅해야 했고, 그래서 실제로는
       * 대부분 비어 있었다. 모델을 고르면 그 모델의 자동 적용 변경점이 미리
       * 체크돼 들어오고, 필요하면 검색으로 더하거나 체크를 해제한다.
       * 편집 모드에는 넣지 않는다 — 이미 있는 로봇의 변경점은 적용일·해제
       * 이력이 중요해 부품 탭이 계속 단일 창구다.
       */}
      {mode === 'create' && (
        <EcnRegistrationPicker modelId={selectedModel?.id} onChange={(ids) => set('ecnIds', ids)} />
      )}

      {/*
       * 계획서(2026-08-27) 요구 4 — 등록하면서 로더를 함께 장착한다(선택).
       * 종전엔 로봇을 만든 뒤 로봇 상세에서 한 번 더 눌러야 했고, 그 사이에
       * 실패하면 로더 없는 로봇이 남았다. 서버가 등록과 장착을 한 트랜잭션으로
       * 처리하므로 장착이 거절되면 로봇 등록도 함께 취소된다.
       *
       * 후보는 **재고(IN_STOCK)** 로더만 — 이미 다른 로봇에 붙은 것은 서버가
       * 거절하므로 목록에 올리지 않는다(누르면 실패할 선택지를 주지 않는다).
       * 로더↔기종 제약은 서버에 없으므로 화면에서도 걸지 않는다 — 없는 규칙을
       * 화면만 갖고 있으면 서버와 갈린다.
       */}
      {mode === 'create' && (
        <LoaderPickerField value={values.loaderUnitId} onChange={(id) => set('loaderUnitId', id)} />
      )}

      {/*
       * 수정 사항2.pdf p.5 #1+#2 — 잠금 정책 재정비.
       *   - 소유 법인: edit 모드에서 ADMIN 에 한해 변경 가능. 변경 시
       *     상위 modal 이 reason 을 묻고 /robots/:id/transfer 호출하여
       *     AssetTransferEvent 감사 로그를 남김. "(잠금)" 마커는
       *     "감사 이벤트 기록됨" 의미로 유지.
       *   - 상태 / 위치 / 검증 상태: 실제로는 enum picklist 가 전부 노출되어
       *     늘 편집 가능했음. "(잠금)" 마커가 "수정 불가" 로 오해되어 제거.
       *     (코드 통제 enum 인 점은 hint 텍스트로 대체 가능.)
       */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field
          label="소유 법인"
          htmlFor="ownerEntity"
          required
          locked
          hint={
            mode === 'edit'
              ? '관리자만 바꿀 수 있습니다. 바꾸면 이관 기록이 자동으로 남습니다.'
              : undefined
          }
        >
          <Select
            id="ownerEntity"
            value={values.ownerEntity}
            // ADMIN 은 create + edit 모두 수정 가능. 비-ADMIN 은 항상 잠금.
            disabled={entityDisabled}
            onChange={(e) => set('ownerEntity', e.target.value as OwnerEntity)}
          >
            <option value={OwnerEntity.BEYOND_HONEYCOMB}>비욘드허니컴</option>
            <option value={OwnerEntity.HONEYCOMB_ROBOTICS}>허니컴로보틱스</option>
          </Select>
        </Field>

        {/* round11 요구2 — 선택지는 전이 가드를 통과하는 상태만(statusChoices).
            라벨은 공유 정전 소스 ROBOT_STATUS_LABELS 소비. */}
        <Field label="상태" htmlFor="status" hint={statusHint}>
          <Select
            id="status"
            value={values.status}
            onChange={(e) => set('status', e.target.value as RobotStatus)}
          >
            {statusChoices.map((s) => (
              <option key={s} value={s}>
                {ROBOT_STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="위치" htmlFor="locationCategory">
          {/* PDF §Phase A 페이지 3 §3+§4 — 새 로봇 추가 모달도 5개 옵션
              (연구소/공장/창고/고객 현장/폐기) 으로 매칭. AS_CENTER 는
              backward-compat 으로 schema 유지하되 dropdown 에선 노출 X. */}
          <Select
            id="locationCategory"
            value={values.locationCategory}
            onChange={(e) => set('locationCategory', e.target.value as LocationCategory)}
          >
            <option value={LocationCategory.RESEARCH}>연구소</option>
            <option value={LocationCategory.FACTORY}>공장</option>
            <option value={LocationCategory.WAREHOUSE}>창고</option>
            <option value={LocationCategory.CUSTOMER_SITE}>고객 현장</option>
            <option value={LocationCategory.DISPOSAL}>폐기</option>
          </Select>
        </Field>

        <Field label="검증 상태" htmlFor="verificationStatus">
          <Select
            id="verificationStatus"
            value={values.verificationStatus}
            onChange={(e) => set('verificationStatus', e.target.value as VerificationStatus)}
          >
            <option value={VerificationStatus.CONFIRMED}>확인됨</option>
            <option value={VerificationStatus.PHANTOM}>미확인 자산</option>
            <option value={VerificationStatus.TENTATIVE_OWNERSHIP}>소유 재검토</option>
            <option value={VerificationStatus.MISSING_SERIAL}>SN 소실</option>
          </Select>
        </Field>
      </div>

      {/*
        §D-1 #4 / Q#5 (PDF p.8) — 회계 번호 / 회계 Scheme / 첫 제조 월 /
        첫 출고 월 inputs are deliberately HIDDEN in the new UI but the
        underlying values still round-trip through `RobotFormValues` so
        edits to any other field don't blank them out for legacy data.
        Re-introduce as an admin-only collapsible if accounting needs
        access (separate follow-up).
      */}

      {/*
        Plan v0.3.0 §H-3 (PDF p.8 §D-1 #3) — operator-editable
        trace field. Color uses <EditableSelect> backed by
        /option-groups (groupKey = robot.color). Manager+ users can
        add new values inline via the footer "+ 새 옵션 추가…" entry.
        Free-text legacy values stored in the DB still resolve via the
        OptionValue's `label ?? value` fallback in EditableSelect.

        FB2 §5C (결정 D) — "로더 종류"(loaderType) 입력 제거. 구분 사양
        (variantValue)과 의미가 중복되어 폼/상세에서 모두 제거한다. DB 컬럼
        Robot.loaderType 은 보존(파괴적 마이그레이션 회피); 단지 폼에서 더
        이상 읽고/쓰지 않는다.
      */}
      {/* create 모드에선 위 SN Composer 가 색상(ColorMode 첫 글자)을 소유하므로,
          동일한 values.color 를 두 번 노출하지 않도록 edit 모드에서만 표시한다. */}
      {mode === 'edit' && (
        <Field
          label="색상"
          htmlFor="color"
          hint="매니저는 '+ 새 옵션 추가…'로 항목을 늘릴 수 있습니다"
        >
          <EditableSelect
            id="color"
            groupKey="robot.color"
            value={values.color ?? ''}
            onChange={(v) => set('color', v)}
            placeholder="— 선택 —"
          />
        </Field>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* round5 #3a — create+컴포저(자동) 모드에선 생산일자가 제조 완료일로
            서버 파생되므로 중복 입력을 숨긴다. 직접입력(manual) 모드와 edit
            모드는 입력 유지. */}
        {mode === 'create' && !snManual ? (
          <Field
            label="제조 완료일"
            htmlFor="manufactureCompletedAt"
            hint="생산일자에 맞춰 자동으로 바뀝니다"
          >
            <Input
              id="manufactureCompletedAt"
              type="date"
              value={values.productionDate ?? ''}
              disabled
            />
          </Field>
        ) : (
          <Field label="제조 완료일" htmlFor="manufactureCompletedAt">
            <Input
              id="manufactureCompletedAt"
              type="date"
              value={values.manufactureCompletedAt ?? ''}
              onChange={(e) => set('manufactureCompletedAt', e.target.value)}
            />
          </Field>
        )}

        <Field
          label="바이너리 업데이트일"
          htmlFor="binaryUpdatedAt"
          hint={
            mode === 'create'
              ? '입력하면 펌웨어 탭에 바이너리 이력이 함께 기록됩니다'
              : '이력은 펌웨어 탭에서 추가합니다. 이 값은 최신 이력으로 자동 갱신됩니다.'
          }
        >
          <Input
            id="binaryUpdatedAt"
            type="date"
            value={values.binaryUpdatedAt ?? ''}
            onChange={(e) => set('binaryUpdatedAt', e.target.value)}
          />
        </Field>
      </div>

      {/*
        초기 데이터 보정 — 첫 제조월 / 첫 출고월 / 회계 장부 No 편집(편집 모드 전용).
        이 세 필드는 생성 폼에서 숨겨져 있어 자동 적재분이 비어 있었고, 첫 제조/출고
        월은 기존 PATCH 경로에도 없었다(읽기 전용). 관리자 정정용으로 노출한다.
          - 첫 제조/출고 월: YYMM 4자리(예: 2504 = 2025년 4월). 비제한.
          - 회계 장부 No: ADMIN 전용(@DeniedForRole) → ADMIN 에게만 입력란 노출.
      */}
      {mode === 'edit' && (
        <div
          style={{
            marginTop: 4,
            paddingTop: 12,
            borderTop: '1px dashed var(--border-subtle)',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-default)' }}>
            관리자 정정: 이력과 회계
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field
              label="첫 제조 월 (YYMM)"
              htmlFor="firstProductionYm"
              hint="4자리로 입력합니다. 예: 2504는 2025년 4월"
            >
              <Input
                id="firstProductionYm"
                value={values.firstProductionYm ?? ''}
                onChange={(e) => set('firstProductionYm', e.target.value)}
                placeholder="예: 2504"
                inputMode="numeric"
                maxLength={4}
                style={{ fontFamily: 'var(--font-mono)' }}
              />
            </Field>
            <Field
              label="첫 출고 월 (YYMM)"
              htmlFor="firstShipmentYm"
              hint="4자리로 입력합니다. 예: 2505는 2025년 5월"
            >
              <Input
                id="firstShipmentYm"
                value={values.firstShipmentYm ?? ''}
                onChange={(e) => set('firstShipmentYm', e.target.value)}
                placeholder="예: 2505"
                inputMode="numeric"
                maxLength={4}
                style={{ fontFamily: 'var(--font-mono)' }}
              />
            </Field>
          </div>
          {isAdmin && (
            <Field
              label="회계 장부 번호"
              htmlFor="accountingBookNo"
              hint="자산 대장의 회계 번호입니다. 관리자만 수정할 수 있습니다."
            >
              <Input
                id="accountingBookNo"
                value={values.accountingBookNo ?? ''}
                onChange={(e) => set('accountingBookNo', e.target.value)}
                placeholder="예: 25_GEN2_001"
              />
            </Field>
          )}
        </div>
      )}
      {/* round5 #4 — 등록 시 BINARY 이벤트의 버전(선택). 미입력 시 '미상'. */}
      {mode === 'create' && values.binaryUpdatedAt && (
        <Field
          label="바이너리 버전 (선택)"
          htmlFor="binaryVersion"
          hint="예: 1.2.3. 펌웨어 탭에 표시됩니다."
        >
          <Input
            id="binaryVersion"
            value={values.binaryVersion ?? ''}
            onChange={(e) => set('binaryVersion', e.target.value)}
            placeholder="미상"
            style={{ fontFamily: 'var(--font-mono)' }}
          />
        </Field>
      )}

      <Field label="메모" htmlFor="notes">
        <Textarea
          id="notes"
          value={values.notes ?? ''}
          onChange={(e) => set('notes', e.target.value)}
          rows={3}
        />
      </Field>
    </form>
  );
}

// ============================================================
// 개선계획 §4 — 적용 변경점 선택 (create 모드 전용)
// ============================================================
//
// 모델을 고르면 그 모델의 "자동 적용" 변경점을 미리 체크된 상태로 제안한다
// (오너 결정 ①A — 신규 등록분에만 적용. 이미 있는 재고/출고 로봇에는 소급되지
// 않으며, 소급은 개조 관리의 일괄 적용이 담당).
//
// 체크/해제는 사용자가 명시적으로 만진 것만 기억한다(pinned/dropped). 모델을
// 바꾸면 프리체크 목록은 새 모델 기준으로 다시 계산되지만, 사용자가 직접 더한
// 항목은 남고 직접 뺀 항목은 다시 살아나지 않는다.
//
// 모델이 다른 변경점도 [전체 보기] 로 찾아서 붙일 수 있다 — 서버에도 모델
// 일치 가드는 없다(soft).
// 선택 상태는 이 컴포넌트가 소유한다(비제어) — 최종 선택이 "모델별 자동 후보 -
// 직접 뺀 것 + 직접 더한 것"의 파생값이라 부모 값으로 되돌려 받을 수 없다.
// 부모는 emit 된 결과를 form values 에 담아 제출에만 쓴다.
function EcnRegistrationPicker({
  modelId,
  onChange,
}: {
  modelId?: string;
  onChange: (ids: string[]) => void;
}) {
  const [keyword, setKeyword] = useState('');
  const [showAll, setShowAll] = useState(false);
  // 사용자가 직접 켠 것 / 끈 것. 자동 후보와 합쳐 최종 선택을 만든다.
  const [pinned, setPinned] = useState<ReadonlySet<string>>(() => new Set());
  const [dropped, setDropped] = useState<ReadonlySet<string>>(() => new Set());
  // 목록에서 빠져도(모델 변경 등) 선택된 항목의 라벨을 계속 보여주기 위한 캐시.
  const [known, setKnown] = useState<Record<string, EcnSummaryDto>>({});

  const autoQ = useQuery({
    queryKey: ['ecns', 'auto-apply', modelId ?? ''],
    queryFn: () => ecnsApi.list({ modelId, autoApply: true }),
    enabled: Boolean(modelId),
    staleTime: 60_000,
  });

  const trimmedKeyword = keyword.trim();
  const searchQ = useQuery({
    queryKey: ['ecns', 'reg-search', showAll ? '' : (modelId ?? ''), trimmedKeyword],
    queryFn: () => ecnsApi.list({ q: trimmedKeyword, modelId: showAll ? undefined : modelId }),
    enabled: trimmedKeyword.length > 0,
    staleTime: 30_000,
  });

  // ECN 카탈로그는 로봇/로더가 공유하는 한 테이블이라 로더 개조에만 걸린
  // 변경점이 로봇 등록 후보로 섞여 나온다. 기본 후보에서만 걷어내고
  // [전체 보기]에서는 그대로 보여준다(하드 가드 아님).
  const autoItems = useMemo(
    () => (autoQ.data?.items ?? []).filter((e) => !e.loaderRevLinkCount),
    [autoQ.data],
  );
  const searchItems = useMemo(
    () => (searchQ.data?.items ?? []).filter((e) => showAll || !e.loaderRevLinkCount),
    [searchQ.data, showAll],
  );

  useEffect(() => {
    const seen = [...autoItems, ...searchItems];
    if (seen.length === 0) return;
    setKnown((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const e of seen) {
        if (!next[e.id]) {
          next[e.id] = e;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [autoItems, searchItems]);

  const selected = useMemo(() => {
    const s = new Set(autoItems.map((e) => e.id).filter((id) => !dropped.has(id)));
    for (const id of pinned) s.add(id);
    return s;
  }, [autoItems, dropped, pinned]);

  // 부모(form values)로 전파 — 실제로 바뀐 경우에만(렌더 루프 방지).
  const emittedRef = useRef<string | null>(null);
  useEffect(() => {
    const next = [...selected];
    const key = next.join(',');
    if (emittedRef.current === key) return;
    emittedRef.current = key;
    onChange(next);
  }, [selected]);

  // 표시 순서: 자동 후보 → (목록에서 빠진) 선택 항목 → 검색 결과.
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const out: EcnSummaryDto[] = [];
    const push = (e: EcnSummaryDto | undefined): void => {
      if (!e || seen.has(e.id)) return;
      seen.add(e.id);
      out.push(e);
    };
    for (const e of autoItems) push(e);
    for (const id of selected) push(known[id]);
    for (const e of searchItems) push(e);
    return out;
  }, [autoItems, searchItems, selected, known]);

  const autoIdSet = useMemo(() => new Set(autoItems.map((e) => e.id)), [autoItems]);

  function toggle(id: string, checked: boolean): void {
    setPinned((prev) => {
      const n = new Set(prev);
      if (checked) n.add(id);
      else n.delete(id);
      return n;
    });
    setDropped((prev) => {
      const n = new Set(prev);
      if (checked) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  return (
    <Field
      label="적용 변경점"
      hint="모델을 고르면 자동 적용 변경점이 미리 선택됩니다. 신규 등록분에만 반영되며 기존 로봇에는 소급되지 않습니다."
    >
      <div style={ecnPickerWrap} data-testid="ecn-reg-picker">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="설명이나 코드로 변경점 검색"
            aria-label="변경점 검색"
            data-testid="ecn-reg-search"
            style={{ flex: 1 }}
          />
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              whiteSpace: 'nowrap',
            }}
          >
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              aria-label="전체 보기"
              data-testid="ecn-reg-show-all"
            />
            전체 보기
          </label>
        </div>

        {!modelId && (
          <div style={ecnPickerNote}>모델을 먼저 선택하면 자동 적용 변경점을 제안합니다.</div>
        )}
        {modelId && autoQ.isLoading && <div style={ecnPickerNote}>불러오는 중…</div>}
        {modelId && !autoQ.isLoading && rows.length === 0 && (
          <div style={ecnPickerNote}>
            {trimmedKeyword
              ? '검색 결과가 없습니다.'
              : '이 모델에 자동 적용할 변경점이 없습니다. 검색으로 추가할 수 있습니다.'}
          </div>
        )}

        {rows.length > 0 && (
          <div style={ecnPickerList}>
            {rows.map((ecn) => {
              const checked = selected.has(ecn.id);
              return (
                <label key={ecn.id} style={ecnPickerRow}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => toggle(ecn.id, e.target.checked)}
                    data-testid={`ecn-reg-check-${ecn.id}`}
                  />
                  <Tooltip content={ecnTooltip(ecn)} focusable={false} style={{ minWidth: 0 }}>
                    <span style={{ fontSize: 13 }}>{formatEcnLabel(ecn)}</span>
                  </Tooltip>
                  {autoIdSet.has(ecn.id) && <span style={autoBadge}>자동</span>}
                </label>
              );
            })}
          </div>
        )}
      </div>
    </Field>
  );
}

// "YYYY-MM-DD · 설명 (코드)" — 변경점 카탈로그/부품 탭과 같은 표기.
//
// 추가수정 2차(26-07-27) 정정 — EcnCatalogPage.formatEcnLabel 의 복제본이라
// 카탈로그가 KST 로 바뀐 뒤 이 화면만 UTC 로 남아 있었다. 값 자체는 어긋나지
// 않는다: firstAppliedAt 은 항상 T00:00:00.000Z(UTC 자정)로 저장돼 +9h 해도
// 같은 날(09:00 KST)이라 UTC 자르기와 KST 환산 결과가 일치한다. 그럼에도
// 맞추는 이유는 **표기 일관성** — 같은 한 줄 표기를 만드는 코드가 두 벌이면
// 다음에 한쪽만 또 어긋난다. 구분자는 카탈로그와 같은 하이픈 유지.
function formatEcnLabel(ecn: EcnSummaryDto): string {
  const date = kstYmd(ecn.firstAppliedAt)?.replace(/\./g, '-') ?? null;
  const head = date ? `${date} · ${ecn.description}` : ecn.description;
  return `${head} (${ecn.code})`;
}

function ecnTooltip(ecn: EcnSummaryDto): string {
  return [
    ecn.code,
    ecn.description,
    `최초 적용일 ${kstYmd(ecn.firstAppliedAt)?.replace(/\./g, '-') ?? '미지정'}`,
    `적용 로봇 ${ecn.appliedRobotCount}대`,
  ].join('\n');
}

const ecnPickerWrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 10,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-subtle)',
};

const ecnPickerNote: CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-muted)',
};

const ecnPickerList: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  maxHeight: 180,
  overflow: 'auto',
};

const ecnPickerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px 2px',
  cursor: 'pointer',
};

const autoBadge: CSSProperties = {
  fontSize: 10,
  padding: '1px 6px',
  borderRadius: 'var(--r-pill)',
  border: '1px solid var(--border-default)',
  color: 'var(--fg-muted)',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

// ============================================================
// S2 Phase 2.2 — 자동 Long-SN Composer (create 모드 전용)
// ============================================================
//
// 운영자가 원시 SN 문자열(`GEN2-2613-WF207`)을 직접 타이핑하는 대신,
// 부품(모델/생산일자/색상/불감지/PC모듈)을 고르면 실시간으로 Long SN 을
// 조립하고 `SERIAL_LONG_RE` 로 검증한다. 조립된 값은 매 변경 시
// `values.serialLong` 에 기록되어 제출에 그대로 사용된다.
//
//   modelPrefix  = 선택 모델의 shortPrefix (code 매칭). UNKNOWN/"-" → 수동.
//   sensorless   = shortPrefix ∈ {SEN, HL} → ColorMode(색상+불감지) 생략.
//   생산주(YYWW) = productionWeekFromDate(생산일자), 읽기 전용 표시.
//   PC모듈        = 말미 serial 숫자(예: "207"). DB 컬럼 없이 SN 문자열에만 기여.
//
// "직접 입력 (예외 케이스)" 체크 시 컴포저를 숨기고 원시 serialLong input
// 으로 폴백 (규격 외 SN 이나 마이그레이션 케이스 대응).
const SENSORLESS_PREFIXES = ['SEN', 'HL'];

interface SnComposerProps {
  values: RobotFormValues;
  set: <K extends keyof RobotFormValues>(key: K, value: RobotFormValues[K]) => void;
  // round5 #3b 회귀수정 — 여러 필드를 "한 번의 onChange" 로 갱신. 별도
  // effect 가 각각 set() 을 부르면 둘 다 같은 stale values 스냅샷을 spread
  // 해 나중 호출이 앞 호출의 필드를 덮어쓴다(serialLong 이 undefined 로
  // 클로버되던 버그 — 이전 검증에서 vitest | grep 파이프가 exit 를 가려
  // 머지를 통과했었음).
  patch: (p: Partial<RobotFormValues>) => void;
  // round5 #3a — 직접입력 토글은 부모(RobotForm)가 소유: 제조 완료일 입력의
  // 표시 여부가 이 상태에 묶이기 때문.
  manual: boolean;
  onManualChange: (v: boolean) => void;
}

function SnComposer({ values, set, patch, manual, onManualChange }: SnComposerProps) {
  // PC모듈은 DB 컬럼이 없는 컴포저-로컬 상태.
  const [pcModule, setPcModule] = useState('');

  const { data } = useQuery<ListModelsResponse>({
    queryKey: ['models'],
    queryFn: () => modelsApi.list(),
    staleTime: 5 * 60_000,
  });
  const models = data?.items ?? [];

  // 선택된 모델 → shortPrefix. modelCode 는 model.code 이므로 code 로 매칭.
  const selectedModel = models.find((m) => m.code === values.modelCode);
  const modelPrefix = selectedModel?.shortPrefix ?? '';
  // UNKNOWN 모델("-") 이나 미선택은 유효한 SN 을 만들 수 없으므로 수동 취급.
  const prefixUnusable = modelPrefix === '' || modelPrefix === '-';
  const sensorless = SENSORLESS_PREFIXES.includes(modelPrefix);
  // FB2 §5B — 모델이 고정 ColorMode(예: Zephyr 'S2')를 가지면 색상·불감지 대신
  // 그 토큰을 ColorMode 로 직접 사용한다. 데이터 주도(서버 /models 노출)라
  // 새 센서리스 모델이 늘어도 코드 변경 없이 동작한다.
  const fixedColorMode = selectedModel?.fixedColorMode ?? null;
  // 색상·불감지 입력 숨김 조건: 센서리스(ColorMode 없음) 또는 고정 ColorMode(S2 등).
  const hideColorSensor = sensorless || fixedColorMode != null;

  const productionDate = values.productionDate ?? '';
  // 생산일자 → 생산주(YYWW). 빈/비정상 날짜는 빈 문자열로 폴백.
  let productionWeek = '';
  if (productionDate) {
    const d = new Date(productionDate);
    if (!Number.isNaN(d.getTime())) productionWeek = productionWeekFromDate(d);
  }

  // 실시간 조립값(미리보기). prefix/주차/PC모듈 이 모두 있을 때만 생성.
  const canCompose = !prefixUnusable && productionWeek !== '' && pcModule.trim() !== '';
  const composed = canCompose
    ? composeSerialLong({
        modelPrefix,
        productionWeek,
        color: values.color,
        sensorType: values.sensorType,
        pcModule: pcModule.trim(),
        sensorless,
        // FB2 §5B — 값이 있으면 composer 가 색상+불감지 대신 이 토큰을 ColorMode 로 사용.
        fixedColorMode: fixedColorMode ?? undefined,
      })
    : '';
  const valid = composed !== '' && SERIAL_LONG_RE.test(composed);

  // 조립값을 serialLong 에 동기화 (수동 모드에선 컴포저가 값을 건드리지 않음).
  // 렌더 중 setState 루프를 피하기 위해 effect 로 처리하고, 실제 값이 바뀐
  // 경우에만 부모에 전파한다.
  const nextSerialLong = manual ? undefined : composed || undefined;

  // round5 #3b — Short SN 자동 파생. 현장 규칙(Short = PC 모듈, Zephyr 만
  // '2' 접두)은 Long SN 구조에 내재하므로 조립값에서 파생한다
  // (deriveSerialShortFromLong — 모델 메타데이터 주도, 하드코딩 없음).
  // 사용자가 자동값과 다른 값을 직접 입력하면 dirty 로 보고 동기화를 멈추되
  // (레거시 예외 허용), 불일치 hint 를 비차단으로 표시한다.
  const derivedShort = !manual && valid ? deriveSerialShortFromLong(composed) : null;
  const [shortDirty, setShortDirty] = useState(false);
  const lastAutoShortRef = useRef<string | null>(null);
  useEffect(() => {
    const current = (values.serialShort ?? '').trim();
    if (current && current !== lastAutoShortRef.current && current !== (derivedShort ?? '')) {
      setShortDirty(true);
    }
  }, [values.serialShort, derivedShort]);

  // 조립값 → serialLong + (dirty 아니면) serialShort 를 **한 번의 patch** 로
  // 동기화. (회귀수정 — 별도 effect 2개가 각각 set() 하면 stale spread 로
  // 서로의 필드를 덮어써 serialLong 이 유실됐다.)
  useEffect(() => {
    if (manual) return; // 직접 입력 모드: 컴포저가 SN 을 관리하지 않음.
    const p: Partial<RobotFormValues> = {};
    if ((values.serialLong ?? undefined) !== nextSerialLong) p.serialLong = nextSerialLong;
    if (!shortDirty && derivedShort && (values.serialShort ?? '') !== derivedShort) {
      lastAutoShortRef.current = derivedShort;
      p.serialShort = derivedShort;
    }
    if (Object.keys(p).length > 0) patch(p);
  }, [nextSerialLong, derivedShort, manual, shortDirty, values.serialLong, values.serialShort]);
  const shortMismatch =
    shortDirty && derivedShort !== null && (values.serialShort ?? '').trim() !== derivedShort;

  // FB3 §0 (P0, 결정 0) — Zephyr 등록 차단 수정. 색상·불감지 입력이 숨겨지는
  // 모델(센서리스 또는 고정 ColorMode, 예: Zephyr 'S2')에서는 form state 의
  // values.sensorType / values.color 가 stale 하게 남아 valuesToCreateBody 가
  // 무조건 전송 → >8자 sensorType 이 CreateRobotDto @MaxLength(8) 에 걸려 400.
  // SN 조립은 fixedColorMode 경로라 이 값들과 무관하므로(위 composeSerialLong
  // 참고) 입력이 숨겨질 때 능동적으로 clear 해 body 에서 제외한다. SN sync 와
  // 같은 위치에 둬 응집도를 높인다. 값이 실제로 남아있을 때만 set 을 호출해
  // 렌더 루프를 피한다.
  useEffect(() => {
    if (!hideColorSensor) return;
    if (values.sensorType !== undefined) set('sensorType', undefined);
    if (values.color !== undefined) set('color', undefined);
  }, [hideColorSensor, values.sensorType, values.color]);

  return (
    <div style={composerWrap}>
      <div style={composerHeader}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Long SN 부여</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <input
            type="checkbox"
            checked={manual}
            onChange={(e) => onManualChange(e.target.checked)}
            aria-label="직접 입력"
          />
          직접 입력
        </label>
      </div>

      {manual ? (
        // 폴백: 규격 외/마이그레이션 SN 을 위한 원시 입력.
        <Field
          label="Long SN"
          htmlFor="serialLong"
          hint="GEN2-2613-WF207처럼 입력합니다. Short SN을 입력하면 비워 둬도 됩니다."
        >
          <Input
            id="serialLong"
            value={values.serialLong ?? ''}
            onChange={(e) => set('serialLong', e.target.value)}
            placeholder="예: GEN2-2613-WF207"
            style={{ fontFamily: 'var(--font-mono)' }}
          />
        </Field>
      ) : (
        <>
          {prefixUnusable && (
            <div style={composerNote}>
              모델을 먼저 선택해 주세요. UNKNOWN 모델은 자동으로 만들 수 없으니 직접 입력을 사용해
              주세요.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field
              label="생산일자"
              htmlFor="productionDate"
              hint={`생산주 ${productionWeek || '—'}`}
            >
              <Input
                id="productionDate"
                type="date"
                value={productionDate}
                onChange={(e) => set('productionDate', e.target.value || undefined)}
              />
            </Field>

            <Field label="PC 모듈" htmlFor="pcModule" hint="SN 끝의 일련번호, 예: 207">
              <Input
                id="pcModule"
                value={pcModule}
                onChange={(e) => setPcModule(e.target.value)}
                placeholder="예: 207"
                inputMode="numeric"
                style={{ fontFamily: 'var(--font-mono)' }}
              />
            </Field>
          </div>

          {/* 센서리스(SEN/HL)는 ColorMode 가 없는 별도 포맷, Zephyr 등 고정
              ColorMode(예: S2) 모델은 색상·불감지 대신 고정 토큰을 쓰므로
              두 경우 모두 색상/불감지 입력을 숨긴다 (FB2 §5B). */}
          {!hideColorSensor && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="색상" htmlFor="composerColor" hint="SN 색상 코드의 첫 글자 W, G, S">
                <EditableSelect
                  id="composerColor"
                  groupKey="robot.color"
                  value={values.color ?? ''}
                  onChange={(v) => set('color', v || undefined)}
                  placeholder="— 선택 —"
                />
              </Field>

              <Field label="불감지" htmlFor="sensorType" hint="SN 색상 코드의 두 번째 글자 F, N">
                <EditableSelect
                  id="sensorType"
                  groupKey="robot.sensorType"
                  value={values.sensorType ?? ''}
                  onChange={(v) => set('sensorType', v || undefined)}
                  placeholder="— 선택 —"
                />
              </Field>
            </div>
          )}

          {/* 실시간 미리보기 + 검증 결과. */}
          <div style={previewRow}>
            <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>미리보기</span>
            <code style={previewCode} data-testid="sn-preview">
              {composed || '정보를 입력하면 자동으로 만들어집니다'}
            </code>
            {/* round5 #3b — Short SN 자동 파생 안내 / 불일치 경고(비차단). */}
            {derivedShort && !shortMismatch && (
              <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                Short SN 자동: {derivedShort}
              </span>
            )}
            {shortMismatch && (
              <span style={{ fontSize: 12, color: 'var(--fg-warning)' }}>
                Short SN이 PC 모듈 번호와 다릅니다. 자동값은 {derivedShort}입니다.
              </span>
            )}
            {composed !== '' &&
              (valid ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 12,
                    color: 'var(--fg-success)',
                  }}
                >
                  <Icon name="check" size={12} aria-hidden /> 유효
                </span>
              ) : (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 12,
                    color: 'var(--fg-danger)',
                  }}
                >
                  <Icon name="x" size={12} aria-hidden /> 형식 불일치
                </span>
              ))}
          </div>
        </>
      )}
    </div>
  );
}

const composerWrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: 12,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-subtle)',
};

const composerHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const composerNote: CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-muted)',
};

const previewRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
};

const previewCode: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 13,
  padding: '4px 8px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
};

// round11 요구1 — 폼 입력은 이제 e.target.value 원문을 그대로 담는다(빈 문자열
// 포함). PATCH 는 "빈 문자열 = 지우기" 라 그대로 보내지만, POST(신규 등록)에는
// 지울 값이 없다 — 빈 문자열을 그대로 보내면 @Matches/@MaxLength 가 형식 위반
// 400 을 내거나 DB 에 '' 가 저장된다. create body 에서만 빈 값을 접는다.
const omitEmpty = (s: string | undefined): string | undefined => (s ? s : undefined);

export function valuesToCreateBody(v: RobotFormValues): CreateRobotBody {
  return {
    serialShort: omitEmpty(v.serialShort),
    serialLong: omitEmpty(v.serialLong),
    modelCode: v.modelCode,
    status: v.status,
    locationCategory: v.locationCategory,
    verificationStatus: v.verificationStatus,
    accountingBookNo: omitEmpty(v.accountingBookNo),
    accountingScheme: v.accountingScheme,
    ownerEntity: v.ownerEntity,
    firstProductionYm: omitEmpty(v.firstProductionYm),
    firstShipmentYm: omitEmpty(v.firstShipmentYm),
    // §D-1 (PDF p.8) — operator-editable trace fields
    color: omitEmpty(v.color),
    manufactureCompletedAt: omitEmpty(v.manufactureCompletedAt),
    binaryUpdatedAt: omitEmpty(v.binaryUpdatedAt),
    // round5 #4 — BINARY 펌웨어 이벤트 버전(미입력 시 서버가 '미상').
    binaryVersion: omitEmpty(v.binaryVersion),
    // S2 Phase 2.2 — 자동 SN Composer 의 내구성 입력 (create 전용).
    // productionWeek 은 서버가 productionDate 로부터 파생하므로 보내지 않음.
    productionDate: omitEmpty(v.productionDate),
    sensorType: omitEmpty(v.sensorType),
    // FB §2 — 구분 사양(모델 축 값).
    variantValue: omitEmpty(v.variantValue),
    notes: omitEmpty(v.notes),
    // 개선계획 §4 — 등록 창에서 고른 적용 변경점. 빈 선택은 키 자체를 뺀다.
    ecnIds: v.ecnIds && v.ecnIds.length > 0 ? v.ecnIds : undefined,
    // 계획서(2026-08-27) 요구 4 — 안 고르면 키 자체를 뺀다(서버가 장착을 시도하지 않는다).
    loaderUnitId: omitEmpty(v.loaderUnitId),
  };
}

export function valuesToUpdateBody(
  v: RobotFormValues,
  // 회계 필드(accountingBookNo/Scheme)는 백엔드 @DeniedForRole(ADMIN 외 전 역할 — 2026-10-20 에
  // 영업·필드 매니저까지 막아 주석의 'ADMIN 전용'과 맞췄다) → ADMIN 전용. 비-ADMIN 이 값을 본문에 담아 보내면 **미변경이라도** 전역
  // DeniedForRoleInterceptor 가 403 을 던진다(필드 존재만으로 거부). 따라서
  // 권한이 있을 때만 본문에 포함한다. (기존엔 항상 포함 → MANAGER 편집 시 회계
  // 번호가 채워진 로봇에서 403 잠복 버그)
  opts?: { canEditAccounting?: boolean },
): UpdateRobotBody {
  // round11 요구1 — '채운 값 비우기' 클래스 버그.
  //   폼이 빈 입력을 undefined 로 접으면 JSON.stringify 가 키를 통째로 지우고,
  //   서버의 `if (dto.notes !== undefined)` 는 그것을 '미변경'으로 읽는다
  //   → 지우려는 의도가 서버에 **도달조차 못 했다**(비고/색상/구분사양/SN/
  //   제조월/출고월 전부 동일 증상). 이제 원문(빈 문자열 포함)을 그대로 보내고
  //   서버가 `'' → null` 로 정규화한다.
  const body: UpdateRobotBody = {
    // §D-1 #2 (PDF p.8) — SN now editable on update
    serialShort: v.serialShort,
    serialLong: v.serialLong,
    modelCode: v.modelCode,
    status: v.status,
    locationCategory: v.locationCategory,
    verificationStatus: v.verificationStatus,
    // §D-1 — operator-editable trace fields
    color: v.color,
    // FB §2 — 구분 사양(모델 축 값).
    variantValue: v.variantValue,
    manufactureCompletedAt: v.manufactureCompletedAt,
    binaryUpdatedAt: v.binaryUpdatedAt,
    // 초기 데이터 보정 — 첫 제조월/출고월(YYMM). 빈 값은 undefined 로 흘려보내
    // PATCH 본문에서 제외(미변경 no-op).
    firstProductionYm: v.firstProductionYm,
    firstShipmentYm: v.firstShipmentYm,
    notes: v.notes,
  };
  if (opts?.canEditAccounting) {
    body.accountingBookNo = v.accountingBookNo;
    body.accountingScheme = v.accountingScheme;
  }
  return body;
}

export function defaultRobotFormValues(owner: OwnerEntity): RobotFormValues {
  return {
    modelCode: 'UNKNOWN',
    status: RobotStatus.IN_PRODUCTION,
    locationCategory: LocationCategory.FACTORY,
    verificationStatus: VerificationStatus.CONFIRMED,
    accountingScheme: AccountingScheme.UNKNOWN,
    ownerEntity: owner,
  };
}

/**
 * 등록 창의 로더 선택칸(선택 입력).
 *
 * 재고 로더만 후보로 올린다 — 이미 장착된 로더는 서버가 거절하므로 목록에 두면
 * "고를 수는 있는데 저장만 실패"가 된다.
 */
function LoaderPickerField({
  value,
  onChange,
}: {
  value?: string;
  onChange: (id: string | undefined) => void;
}) {
  const q = useQuery({
    queryKey: ['loaders', 'in-stock-for-create'],
    queryFn: () => loadersApi.list({ status: LoaderStatus.IN_STOCK }),
    staleTime: 30_000,
  });
  const items = q.data?.items ?? [];

  return (
    <Field label="로더 장착 (선택)" htmlFor="robot-loader">
      <Select
        id="robot-loader"
        data-testid="robot-loader-select"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">장착 안 함</option>
        {items.map((l) => (
          <option key={l.id} value={l.id}>
            {l.serial}
            {l.configCode ? ` · ${l.configCode}` : ''}
          </option>
        ))}
      </Select>
      {!q.isLoading && items.length === 0 && (
        <div className="kuun-caption" data-testid="robot-loader-empty">
          재고 로더가 없습니다. 등록한 뒤 로봇 상세에서 장착할 수 있습니다.
        </div>
      )}
    </Field>
  );
}
